import { useEffect, useState } from 'react';
import { isDrawableModelLine } from '../crag/useModelLine';
import { zonesService } from '../../services/zones';
import type { Climb } from '../../types/climb';
import type { Crag } from '../../types/crag';
import type { Zone } from '../../types/zone';

export interface CragRouteModelSource {
  url: string;
  byteSize: number | null;
  sectorView: Zone['model_view'] | null;
}

/**
 * The 3D model a route is drawn on, if it is drawn on one: its sector's own file,
 * else the crag's. Null for a route with no line on the model in force, where the
 * model would have nothing to say about it.
 *
 * The sector is fetched rather than read off the climb: its file costs a query, and
 * the climb's payload goes out in lists. It is waited for, and a sector that cannot
 * be read means no model: with its own file, the crag's would be another coordinate
 * system and the line would be in the wrong place.
 */
export function useCragRouteModel(climb: Climb | null, crag: Crag | null, cragSlug?: string): CragRouteModelSource | null {
  const drawn = isDrawableModelLine(climb?.model_line);
  const zoneSlug = climb?.zone?.slug ?? null;
  const [fetched, setFetched] = useState<{ slug: string; zone: Zone | null } | null>(null);

  useEffect(() => {
    if (!drawn || !zoneSlug || !cragSlug) return;

    let live = true;
    zonesService
      .getZone(cragSlug, zoneSlug, 'crag')
      .then((zone) => {
        if (live) setFetched({ slug: zoneSlug, zone });
      })
      .catch(() => {
        if (live) setFetched({ slug: zoneSlug, zone: null });
      });

    return () => {
      live = false;
    };
  }, [drawn, zoneSlug, cragSlug]);

  if (!drawn || !crag) return null;

  const zone = zoneSlug ? (fetched?.slug === zoneSlug ? fetched.zone : null) : null;
  if (zoneSlug && !zone) return null;

  if (zone?.model_3d_url) {
    return { url: zone.model_3d_url, byteSize: zone.model_3d_byte_size ?? null, sectorView: zone.model_view ?? null };
  }
  if (!crag.model_3d_url) return null;

  return { url: crag.model_3d_url, byteSize: crag.model_3d_byte_size ?? null, sectorView: zone?.model_view ?? null };
}
